import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { API } from "@/lib/api";

const FORM_LANG = (lng) => (["de", "en", "ar"].includes(lng) ? lng : "en");

export const QuestionnaireForm = ({ data }) => {
  const { t, i18n } = useTranslation();
  const lang = FORM_LANG(i18n.language?.split("-")[0]);
  const [patient, setPatient] = useState({ name: "", email: "", phone: "" });
  const [answers, setAnswers] = useState({});
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const setAnswer = (key, value) => setAnswers((a) => ({ ...a, [key]: value }));
  const toggleMulti = (key, option, checked) => {
    setAnswers((a) => {
      const cur = Array.isArray(a[key]) ? a[key] : [];
      const next = checked ? [...cur, option] : cur.filter((v) => v !== option);
      return { ...a, [key]: next };
    });
  };

  const validate = () => {
    const e = {};
    if (!patient.name.trim()) e.name = t("questionnaire.form.required");
    if (!patient.email.trim()) e.email = t("questionnaire.form.required");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(patient.email)) e.email = t("questionnaire.form.invalidEmail");
    if (!consent) e.consent = t("questionnaire.form.consentRequired");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/questionnaires`, {
        type: data.slug,
        language: lang,
        patient,
        answers,
        consent,
      });
      setDone(true);
    } catch {
      toast.error(t("questionnaire.form.error"));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="mx-auto max-w-2xl rounded-3xl bg-sand p-10 text-center" data-testid="questionnaire-success">
        <CheckCircle2 className="mx-auto h-10 w-10 text-champagne-gold" />
        <h2 className="mt-4 font-serif text-2xl text-deep-ink">{t("questionnaire.form.successTitle")}</h2>
        <p className="mt-2 text-deep-ink/70">{t("questionnaire.form.successText")}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl" data-testid={`questionnaire-form-${data.slug}`}>
      <div className="rounded-3xl bg-sand p-6 lg:p-8">
        <h3 className="font-serif text-xl text-deep-ink">{t("questionnaire.form.yourInfo")}</h3>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label={t("questionnaire.form.name")} error={errors.name}>
            <Input data-testid="qf-name" value={patient.name} onChange={(e) => setPatient({ ...patient, name: e.target.value })} className="bg-warm-ivory" />
          </Field>
          <Field label={t("questionnaire.form.email")} error={errors.email}>
            <Input data-testid="qf-email" type="email" value={patient.email} onChange={(e) => setPatient({ ...patient, email: e.target.value })} className="bg-warm-ivory" />
          </Field>
          <Field label={t("questionnaire.form.phone")}>
            <Input data-testid="qf-phone" value={patient.phone} onChange={(e) => setPatient({ ...patient, phone: e.target.value })} className="bg-warm-ivory" />
          </Field>
        </div>
      </div>

      {data.sections.map((sec, si) => (
        <div key={si} className="mt-8">
          <h3 className="font-serif text-2xl text-deep-ink">{sec.heading[lang]}</h3>
          {sec.subheading && <p className="mt-1 text-sm text-deep-ink/60">{sec.subheading[lang]}</p>}
          <div className="mt-5 space-y-6">
            {sec.items.map((item, ii) => (
              <ItemRenderer key={ii} item={item} lang={lang} answers={answers} setAnswer={setAnswer} toggleMulti={toggleMulti} />
            ))}
          </div>
        </div>
      ))}

      <div className="mt-10 rounded-2xl bg-sand/60 p-5">
        <label className="flex items-start gap-3 text-sm text-deep-ink/80">
          <Checkbox data-testid="qf-consent" checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" />
          <span>{t("questionnaire.form.consent")}</span>
        </label>
        {errors.consent && <p className="mt-2 text-sm text-terracotta">{errors.consent}</p>}
      </div>

      <Button
        onClick={submit}
        disabled={submitting}
        data-testid="qf-submit"
        className="mt-6 w-full gap-2 rounded-full bg-deep-ink py-6 text-warm-ivory hover:bg-champagne-gold hover:text-deep-ink"
      >
        {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
        {t("questionnaire.form.submit")}
      </Button>
    </div>
  );
};

const Field = ({ label, error, children }) => (
  <div>
    <Label className="mb-1.5 block text-sm text-deep-ink/70">{label}</Label>
    {children}
    {error && <p className="mt-1 text-sm text-terracotta">{error}</p>}
  </div>
);

const ItemRenderer = ({ item, lang, answers, setAnswer, toggleMulti }) => {
  if (item.kind === "note") {
    return <p className="text-sm italic text-deep-ink/60">{item.text[lang]}</p>;
  }
  if (item.kind === "text_row") {
    return (
      <div
        className="grid gap-4"
        style={{ gridTemplateColumns: item.fields.map((f) => `${f.width}fr`).join(" ") }}
      >
        {item.fields.map((f) => (
          <div key={f.key}>
            <Label className="mb-1.5 block text-sm text-deep-ink/70">{f.label[lang]}</Label>
            <Input data-testid={`qf-${f.key}`} value={answers[f.key] || ""} onChange={(e) => setAnswer(f.key, e.target.value)} className="bg-warm-ivory" />
          </div>
        ))}
      </div>
    );
  }
  if (item.kind === "text") {
    return (
      <div>
        {item.label && <Label className="mb-1.5 block text-sm text-deep-ink/70">{item.label[lang]}</Label>}
        {item.multiline ? (
          <Textarea data-testid={`qf-${item.key}`} rows={3} value={answers[item.key] || ""} onChange={(e) => setAnswer(item.key, e.target.value)} className="bg-warm-ivory" />
        ) : (
          <Input data-testid={`qf-${item.key}`} value={answers[item.key] || ""} onChange={(e) => setAnswer(item.key, e.target.value)} className="bg-warm-ivory" />
        )}
      </div>
    );
  }
  if (item.kind === "radio") {
    return (
      <div>
        {item.question && <Label className="mb-2 block text-sm text-deep-ink/70">{item.question[lang]}</Label>}
        <RadioGroup
          data-testid={`qf-${item.key}`}
          value={answers[item.key] || ""}
          onValueChange={(v) => setAnswer(item.key, v)}
          className={`grid gap-x-6 gap-y-2 ${item.columns >= 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}
        >
          {item.options.map((opt, oi) => (
            <label key={oi} className="flex items-center gap-2 text-sm text-deep-ink/80">
              <RadioGroupItem value={opt[lang]} />
              {opt[lang]}
            </label>
          ))}
        </RadioGroup>
      </div>
    );
  }
  if (item.kind === "checkboxes") {
    const selected = Array.isArray(answers[item.key]) ? answers[item.key] : [];
    return (
      <div>
        {item.question && <Label className="mb-2 block text-sm text-deep-ink/70">{item.question[lang]}</Label>}
        <div className={`grid gap-x-6 gap-y-2 ${item.columns >= 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
          {item.options.map((opt, oi) => (
            <label key={oi} className="flex items-center gap-2 text-sm text-deep-ink/80">
              <Checkbox
                checked={selected.includes(opt[lang])}
                onCheckedChange={(v) => toggleMulti(item.key, opt[lang], !!v)}
              />
              {opt[lang]}
            </label>
          ))}
        </div>
      </div>
    );
  }
  if (item.kind === "table") {
    const rowsArr = Array.from({ length: item.rows });
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              {item.headers.map((h, hi) => (
                <th key={hi} className="border-b border-border pb-1.5 text-start font-medium text-deep-ink/70">{h[lang]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rowsArr.map((_, ri) => (
              <tr key={ri}>
                {item.headers.map((_, hi) => {
                  const cellKey = `${item.key}_${ri}_${hi}`;
                  return (
                    <td key={hi} className="py-1.5 pe-3">
                      <Input data-testid={`qf-${cellKey}`} value={answers[cellKey] || ""} onChange={(e) => setAnswer(cellKey, e.target.value)} className="bg-warm-ivory" />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  return null;
};
